import { patchState, signalStoreFeature, type, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { Observable, of, switchMap, tap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { UmmeldungService } from '../../../service/ummeldung/ummeldung.service';
import { Dispatcher, eventGroup } from '@ngrx/signals/events';
import { UmmeldungLockStatusDto } from '../../../../generated/model/ummeldung-lock-status-dto.model';
import { catchError } from 'rxjs/operators';
import {
  GetUmmeldungLockStatusRequestParams,
  LockUmmeldungRequestParams,
  UnlockUmmeldungRequestParams,
} from '../../../..//generated/api/ummeldung.serviceInterface';

export interface UmmeldungFeatureState {
  ummeldungError: HttpErrorResponse | null;
  ummeldungLoading: boolean;
  ummeldungLockError: HttpErrorResponse | null;
  ummeldungLockLoading: boolean;
}

const state: UmmeldungFeatureState = {
  ummeldungError: null,
  ummeldungLoading: false,
  ummeldungLockError: null,
  ummeldungLockLoading: false,
};

export function withUmmeldungFeatures() {
  return signalStoreFeature(
    withState(state),
    withMethods((store, ummeldungService = inject(UmmeldungService), dispatcher = inject(Dispatcher)) => ({
      archiveUmmeldung(ummeldungId: string, lockRequestParams: LockUmmeldungRequestParams): Observable<unknown> {
        patchState(store, { ummeldungLoading: true });
        return ummeldungService.archiveUmmeldung({ id: ummeldungId }).pipe(
          switchMap(() => this.unlockUmmeldung(lockRequestParams)),
          tap({
            next: () => dispatcher.dispatch(ummeldungEvents.ummeldungCompleted()),
            error: (error: HttpErrorResponse) => patchState(store, { ummeldungError: error }),
            finalize: () => patchState(store, { ummeldungLoading: false }),
          }),
        );
      },
      unlockUmmeldung(param: UnlockUmmeldungRequestParams): Observable<void | object> {
        patchState(store, { ummeldungLockLoading: true, ummeldungLockError: null });
        return ummeldungService.unlockUmmeldung(param).pipe(
          tap({
            error: (error: HttpErrorResponse) => patchState(store, { ummeldungLockError: error }),
            finalize: () => patchState(store, { ummeldungLockLoading: false }),
          }),
          catchError((error: HttpErrorResponse) => {
            // If we call unlock, but there is no lock, we store the error, but we dont want to block the user.
            if (error.status === 404) {
              return of(null);
            }
            return throwError(() => error);
          }),
        );
      },
      getUmmeldungLockStatus(param: GetUmmeldungLockStatusRequestParams): Observable<UmmeldungLockStatusDto> {
        patchState(store, { ummeldungLockLoading: true });
        return ummeldungService.getUmmeldungLockStatus(param).pipe(
          tap({
            error: (error: HttpErrorResponse) => patchState(store, { ummeldungLockError: error }),
            finalize: () => patchState(store, { ummeldungLockLoading: false }),
          }),
        );
      },
    })),
  );
}

export const ummeldungEvents = eventGroup({
  source: 'Ummeldung',
  events: {
    ummeldungCompleted: type<void>(),
  },
});
